import React from 'react';
import { useQuery } from '@apollo/client';
import Button from '../../components/Button';
import ExternalBrandLogo from '../../components/ExternalBrandLogo';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const EXTERNAL_PROFILES_QUERY = gql(/** @complexity 3 */ `
  query externalProfiles($userId: ID!) {
    microsoftIntegration: integration(platform: "microsoft") {
      id
    }
    
    # eslint-disable-next-line @graphql-eslint/require-selections
    slackIntegration: integration(platform: "slack") {
      externalId
    }

    slackUser(slabId: $userId) {
      id
    }
  }
`);

const USER_FRAGMENT = gql(`
  fragment ExternalProfileButtons_user on User {
    id
    email
    identities {
      id
      externalId
      platform
    }
  }
`);

interface ExternalProfileButtonsProps {
  size: 'small' | 'regular';
  user: DocumentType<typeof USER_FRAGMENT>;
}

const ICON_SIZES = { small: 12, regular: 16 };

const ExternalProfileButtons = ({
  user,
  size,
}: ExternalProfileButtonsProps) => {
  const { data } = useQuery(EXTERNAL_PROFILES_QUERY, {
    variables: { userId: user.id },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const slackIdentity = user.identities?.find((i) => i?.platform === 'slack');
  const slackUserId = slackIdentity?.externalId || data?.slackUser?.id;
  const slackTeamId = data?.slackIntegration?.externalId;

  const slackButton =
    slackTeamId && slackUserId ? (
      <Button
        variant="secondary"
        size={size}
        to={`slack://user?team=${slackTeamId}&id=${slackUserId}`}
        icon={<ExternalBrandLogo brand="slack" height={ICON_SIZES[size]} />}
      >
        Slack
      </Button>
    ) : null;

  // https://docs.microsoft.com/en-us/microsoftteams/platform/concepts/build-and-test/deep-links#deep-linking-to-a-chat
  const teamsButton = data?.microsoftIntegration ? (
    <Button
      variant="secondary"
      size={size}
      to={`https://teams.microsoft.com/l/chat/0/0?users=${user.email ?? ''}`}
      newTab
      icon={
        <ExternalBrandLogo
          brand="microsoft"
          product="teams"
          height={ICON_SIZES[size]}
        />
      }
    >
      Teams
    </Button>
  ) : null;

  return (
    <>
      {slackButton}
      {teamsButton}
    </>
  );
};

export default ExternalProfileButtons;
